import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useState
} from "react";

import { formatInTimeZone } from "date-fns-tz";

import { logger } from "src/core/globals";
import {
  FORMAT,
  FORMATS,
  getSpecificDateFormat
} from "src/dataTypes/dateFormats";
import { Brig } from "src/gen/rest/brig/BrigService";
import { Fred } from "src/gen/rest/fred/FredService";
import { TimeFormat } from "src/gen/schema/models/auth";
import { UserPreferences } from "src/gen/schema/models/user";
import { Constants } from "src/utils/constants";
import {
  getDefaultHourCycleForTimeZone,
  getUserTimeZone,
  getUTCTimeZoneAbbreviation,
  UTC
} from "src/utils/timeZone/timeZoneUtils";

interface TimeZoneContextType {
  timeZone: string;
  hourFormat: TimeFormat;
  changeTimeZone: (tz: string) => void;
  changeTimeFormat: (fmt: TimeFormat) => void;
  getFormattedDate: (
    value: Date | number | undefined,
    format: FORMATS,
    showTimeZone?: boolean
  ) => string;
}
export const TimeZoneContext = createContext<TimeZoneContextType>({
  timeZone: UTC,
  hourFormat: TimeFormat.TIME_FORMAT_24HR,
  changeTimeZone: () => {},
  changeTimeFormat: () => {},
  getFormattedDate: () => ""
});

interface TimezoneProviderProps {
  readonly children: ReactNode;
}

export const TimezoneProvider = ({ children }: TimezoneProviderProps) => {
  const [timeZone, setTimeZone] = useState<string>(getUserTimeZone());
  const [hourFormat, setHourFormat] = useState<TimeFormat>(
    getDefaultHourCycleForTimeZone()
  );
  const [userPreferences, setUserPreferences] = useState<UserPreferences>();

  useEffect(() => {
    Fred.getUserPreferences()
      .then((preferences: UserPreferences) => {
        setUserPreferences(preferences);
        setTimeZone(preferences.timezone || getUserTimeZone());
        // If user has not set a time format, use the initial default hour cycle for locale
        if (
          preferences.timeFormat === TimeFormat.TIME_FORMAT_UNSPECIFIED ||
          preferences.timeFormat === TimeFormat.UNRECOGNIZED
        ) {
          const defaultHourFormat = getDefaultHourCycleForTimeZone(
            preferences.timezone
          );
          setHourFormat(defaultHourFormat);
        } else {
          setHourFormat(preferences.timeFormat);
        }
      })
      .catch((err) => {
        logger.error("Get User Preferences Error: ", err);
      });
  }, []);

  const changeTimeZone = useCallback(
    (tz: string) => {
      if (userPreferences) {
        const updatedPreferences = {
          ...userPreferences,
          timezone: tz,
          // UTC special case: force 24HR format
          timeFormat:
            tz === UTC
              ? TimeFormat.TIME_FORMAT_24HR
              : userPreferences.timeFormat
        };
        setUserPreferences(updatedPreferences);
        setTimeZone(tz);
        if (tz === UTC) {
          setHourFormat(TimeFormat.TIME_FORMAT_24HR);
        }
        Brig.updateUserPreferences(updatedPreferences).catch((err) => {
          logger.error("Update User Preferences Error: ", err);
        });
      }
    },
    [userPreferences]
  );

  const changeTimeFormat = useCallback(
    (fmt: TimeFormat) => {
      if (userPreferences) {
        const updatedPreferences = {
          ...userPreferences,
          timeFormat: fmt
        };
        setUserPreferences(updatedPreferences);
        setHourFormat(fmt);
        Brig.updateUserPreferences(updatedPreferences).catch((err) => {
          logger.error("Update User Preferences Error: ", err);
        });
      }
    },
    [userPreferences]
  );

  const getFormattedDate = useCallback(
    (
      value: Date | number | undefined,
      format: FORMATS = FORMAT.LONG,
      showTimeZone = true
    ): string => {
      if (value && value !== -1) {
        const updatedFormatType = getSpecificDateFormat(format, hourFormat);
        return `${formatInTimeZone(
          value,
          timeZone,
          updatedFormatType
        )}${getUTCTimeZoneAbbreviation(timeZone, { enabled: showTimeZone })}`;
      }
      return Constants.EMPTY_VALUE;
    },
    [timeZone, hourFormat]
  );

  return (
    <TimeZoneContext.Provider
      value={{
        timeZone,
        hourFormat,
        changeTimeFormat,
        changeTimeZone,
        getFormattedDate
      }}
    >
      {children}
    </TimeZoneContext.Provider>
  );
};
